/**
 * Settled-probe helper for the fan-out monitoring pages.
 *
 * WHY THIS EXISTS: the diagnostic pages (ErrorDashboard, AuditExplorer,
 * PipelineInspector, PlatformHealth) fan out to many independent RPCs and
 * used to collapse every failure into an empty collection
 * (`.catch(() => [])`). That makes "the runtime returned nothing" and "we
 * could not reach the runtime" **indistinguishable** — and on a page whose
 * job is surfacing trouble, the failure renders as a clean bill of health.
 * An outage looked like health. That is the one direction a monitoring
 * surface must never fail in.
 *
 * WHY NOT JUST RETHROW: these pages deliberately call OPTIONAL RPCs that a
 * given runtime may not implement (an older image, a feature-gated verb).
 * A blanket rethrow would blank the entire page because one optional call
 * is missing — trading a false "all clear" for a false "everything is
 * broken". Both are wrong; the fix is to keep going AND remember which
 * probes failed, so the UI can render "could not reach" beside the ones
 * that did answer.
 */

/** The settled outcome of one probe: either a value, or why it failed. */
export type Probe<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

/** Run a probe to a SETTLED result — never rejects. */
export async function probe<T>(p: Promise<T>): Promise<Probe<T>> {
  try {
    return { ok: true, value: await p };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e ?? "unknown error"),
    };
  }
}

/** The value if the probe succeeded, else `fallback`.
 *  Use ONLY where the caller ALSO surfaces the failure (see
 *  `unreachableNames`) — otherwise this reintroduces the exact bug. */
export function probeOr<T>(p: Probe<T>, fallback: T): T {
  return p.ok ? p.value : fallback;
}

/** Names of the probes that failed, in declaration order. Feed a labelled
 *  record so the UI can name what it could not reach. */
export function unreachableNames(
  probes: Record<string, Probe<unknown>>,
): string[] {
  return Object.entries(probes)
    .filter(([, p]) => !p.ok)
    .map(([name]) => name);
}

/** One-line operator sentence for a partially-failed fan-out, or `null`
 *  when everything answered. Deliberately says the counts are INCOMPLETE
 *  rather than implying the page is merely degraded — an operator reading
 *  "0 errors" needs to know whether that zero is trustworthy. */
export function incompleteNotice(
  unreachable: string[],
  total: number,
): string | null {
  if (unreachable.length === 0) return null;
  if (unreachable.length >= total) {
    return `Could not reach any of the ${total} sources — nothing below is a real reading.`;
  }
  return `${unreachable.length} of ${total} sources unreachable (${unreachable.join(", ")}) — the counts below are INCOMPLETE, not a clean bill of health.`;
}
